/**
 * Sesión del usuario en el front. La sucursal y el rol vienen del servidor (token); la UI
 * solo los MUESTRA y oculta lo no permitido — la defensa real es el servidor (spec 01 regla 6).
 */
import { puede as puedeUsuario, type Nivel, type Permiso, type PermisosEfectivos, type UsuarioSesion } from '@mg/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api, get, onSesionExpirada, onTokenRenovado, post, refrescarToken, setToken } from './api.ts';

interface EstadoAuth {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  iniciarSesion: (email: string, password: string) => Promise<UsuarioSesion>;
  cerrarSesion: () => Promise<void>;
  recargar: () => Promise<void>;
  tomarContexto: (sucursalId: string, motivo: string) => Promise<UsuarioSesion>;
  salirContexto: () => Promise<void>;
  puede: (permiso: Permiso, nivel?: Nivel) => boolean;
}

const AuthContext = createContext<EstadoAuth | null>(null);

type RespuestaSesion = { token: string; expira_en: number; usuario: UsuarioSesion };

/**
 * Sucursal que la dueña está operando en ESTA pestaña (auditoría 2026-10-11, H-11): al recargar se le devuelve el
 * mismo contexto hasta su vencimiento original. No se guarda ningún token: solo qué sucursal, con qué motivo y hasta
 * cuándo. Vive en la pestaña (sessionStorage): al cerrarla se pierde.
 */
const CLAVE_CONTEXTO = 'mg.contexto';
interface ContextoGuardado {
  usuario_id: string;
  sucursal_id: string;
  motivo: string;
  hasta: number;
}
function leerContextoGuardado(): ContextoGuardado | null {
  try {
    const c = JSON.parse(sessionStorage.getItem(CLAVE_CONTEXTO) ?? 'null') as ContextoGuardado | null;
    return c && typeof c.sucursal_id === 'string' && typeof c.motivo === 'string' && typeof c.hasta === 'number' && c.hasta * 1000 > Date.now() ? c : null;
  } catch {
    return null;
  }
}
function guardarContexto(c: ContextoGuardado | null): void {
  try {
    if (c) sessionStorage.setItem(CLAVE_CONTEXTO, JSON.stringify(c));
    else sessionStorage.removeItem(CLAVE_CONTEXTO);
  } catch {
    // Sin almacenamiento de pestaña: al recargar se vuelve a la consola global, como antes
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [tokenGlobal, setTokenGlobal] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const usuarioRef = useRef<UsuarioSesion | null>(null);
  useEffect(() => {
    usuarioRef.current = usuario;
  }, [usuario]);

  const recargar = useCallback(async () => {
    try {
      setUsuario(await get<UsuarioSesion>('/auth/yo'));
    } catch {
      setUsuario(null);
    }
  }, []);

  // Al cargar: intenta renovar con la cookie; si no hay, queda deslogueado.
  useEffect(() => {
    let vivo = true;
    (async () => {
      const token = await refrescarToken();
      if (!vivo) return;
      if (token) {
        // La dueña estaba operando una sucursal en esta pestaña: se le devuelve ese contexto (auditado otra vez)
        const guardado = leerContextoGuardado();
        const restaurado = guardado
          ? await post<RespuestaSesion>('/auth/contexto', { sucursal_id: guardado.sucursal_id, motivo: guardado.motivo, restaurar_hasta: guardado.hasta }, { sinRefresh: true }).catch(() => null)
          : null;
        if (!vivo) return;
        if (guardado && restaurado && restaurado.usuario.id === guardado.usuario_id) {
          setTokenGlobal(token);
          setToken(restaurado.token);
          setUsuario(restaurado.usuario);
        } else {
          guardarContexto(null);
          await recargar();
        }
      }
      setCargando(false);
    })();
    onSesionExpirada(() => {
      setUsuario(null);
      queryClient.clear();
    });
    // El token renovado con la cookie NO conserva el contexto de sucursal del superadmin (ADR-0003): se relee la
    // sesión para que la UI no siga mostrando una sucursal que el servidor ya no reconoce (observación 24-09-2026).
    onTokenRenovado(() => {
      const antes = usuarioRef.current;
      if (!antes) return;
      // sinRefresh: un 401 aquí no dispara otra renovación (evita un bucle renovar → releer → renovar)
      void api<UsuarioSesion>('GET', '/auth/yo', { sinRefresh: true })
        .then((u) => {
          if (antes.sucursal_id && !u.sucursal_id && antes.rol === 'superadmin') {
            guardarContexto(null);
            setTokenGlobal(null);
            queryClient.clear();
            toast.info(`Tu operación en ${antes.sucursal_nombre ?? 'la sucursal'} venció`, { description: 'Por seguridad dura un tiempo limitado. Elige la sucursal de nuevo en el selector de arriba.', duration: 8000 });
          }
          setUsuario(u);
        })
        .catch(() => undefined);
    });
    return () => {
      vivo = false;
      onSesionExpirada(null);
      onTokenRenovado(null);
    };
  }, [recargar, queryClient]);

  const iniciarSesion = useCallback(
    async (email: string, password: string) => {
      const r = await post<RespuestaSesion>('/auth/login', { email, password }, { sinRefresh: true });
      guardarContexto(null);
      setToken(r.token);
      setTokenGlobal(null);
      setUsuario(r.usuario);
      queryClient.clear();
      return r.usuario;
    },
    [queryClient],
  );

  const cerrarSesion = useCallback(async () => {
    try {
      await post('/auth/logout', undefined, { sinRefresh: true });
    } finally {
      guardarContexto(null);
      setToken(null);
      setUsuario(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const tomarContexto = useCallback(
    async (sucursalId: string, motivo: string) => {
      const actual = (await import('./api.ts')).getToken();
      const r = await post<RespuestaSesion>('/auth/contexto', { sucursal_id: sucursalId, motivo });
      guardarContexto({ usuario_id: r.usuario.id, sucursal_id: sucursalId, motivo, hasta: r.expira_en });
      setTokenGlobal(actual);
      setToken(r.token);
      setUsuario(r.usuario);
      queryClient.clear();
      return r.usuario;
    },
    [queryClient],
  );

  const salirContexto = useCallback(async () => {
    await post('/auth/contexto/salir').catch(() => undefined);
    guardarContexto(null);
    if (tokenGlobal) setToken(tokenGlobal);
    else await refrescarToken();
    setTokenGlobal(null);
    await recargar();
    queryClient.clear();
  }, [tokenGlobal, recargar, queryClient]);

  const valor = useMemo<EstadoAuth>(
    () => ({
      usuario,
      cargando,
      iniciarSesion,
      cerrarSesion,
      recargar,
      tomarContexto,
      salirContexto,
      // Permisos EFECTIVOS del usuario (plantilla de su rol + lo que la dueña le dio o quitó); el servidor decide igual
      puede: (permiso, nivel = 'total') => (usuario ? puedeUsuario({ rol: usuario.rol, permisos: usuario.permisos as PermisosEfectivos }, permiso, nivel) : false),
    }),
    [usuario, cargando, iniciarSesion, cerrarSesion, recargar, tomarContexto, salirContexto],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): EstadoAuth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}

/** Variante tolerante para componentes compartidos que también se renderizan sin proveedor (tests, impresión). */
export function useAuthOpcional(): EstadoAuth | null {
  return useContext(AuthContext);
}
